<template>
    <VJoyDrawer ref="freelancerDrawer" size="large" @drawer:hide="handleHideDrawer">
        <template #drawer-header>
            <div class="drawer__info-container / flex">
                <VJoyAvatar size="medium" :photo-url="freelancerInfo.photoUrl" :full-name="freelancerInfo.fullName" />
                <div class="drawer__about">
                    <div class="drawer__about__name / flex">
                        <p>{{ freelancerInfo.fullName }}</p>
                        <NuxtLink
                            v-if="freelancerInfo.profileId"
                            target="_blank"
                            rel="noopener noreferrer"
                            external
                            :to="`/profile/${freelancerInfo.profileId}`"
                        >
                            <VJoyIcon name="external-link" size="xxsmall"></VJoyIcon>
                        </NuxtLink>
                    </div>
                    <p v-if="freelancerInfo.headline" class="drawer__about__job-title">{{ freelancerInfo.headline }}</p>
                </div>
            </div>
        </template>
        <template #drawer-body>
            <div class="drawer_wrapper">
                <VJoyTabs v-model:selected-tab="activeTab">
                    <VJoyTab :tab="FreelancerDrawerTab.Profile" :label="t('projectclient.projectview.sidepanel.team.drawer.tab.profile')"></VJoyTab>
                    <VJoyTab
                        v-if="canSeeMessagesTab"
                        :tab="FreelancerDrawerTab.Messages"
                        :label="t('projectclient.projectview.sidepanel.team.drawer.tab.messages')"
                    ></VJoyTab>
                </VJoyTabs>

                <FreelancerProfile
                    v-if="freelancerInfo.profileId"
                    v-show="activeTab === FreelancerDrawerTab.Profile && freelancerInfo.profileId"
                    :profile-id="freelancerInfo.profileId"
                />
                <FreelancerMessenger
                    v-if="canSeeMessagesTab"
                    v-show="canSeeMessagesTab && activeTab === FreelancerDrawerTab.Messages && conversationId"
                    :project-conversation-id="conversationId"
                    :client-identity-id="clientIdentityId"
                    :is-client-owner="isClientOwner"
                />
            </div>
        </template>
    </VJoyDrawer>
</template>

<script setup lang="ts">
    import {computed, onBeforeUnmount, ref, useTemplateRef} from 'vue';
    import {useTranslation} from '#imports';
    import {VJoyAvatar, VJoyDrawer, VJoyIcon, VJoyTab, VJoyTabs} from '@maltjoy/core-vue';
    import FreelancerMessenger from './FreelancerMessenger.vue';
    import FreelancerProfile from './FreelancerProfile.vue';
    import {type FreelancerInfo, FreelancerDrawerTab} from './profile-drawer.types';
    import {eventBus} from '@/common/events';
    import {useUserStore} from '@/common/stores';

    const {t} = useTranslation();

    const props = defineProps<{
        freelancerInfo: FreelancerInfo;
        conversationId: string;
        clientIdentityId: string;
        isClientOwner: boolean;
    }>();

    const userStore = useUserStore();

    const freelancerDrawer = useTemplateRef('freelancerDrawer');

    const activeTab = ref<FreelancerDrawerTab>();

    const canSeeMessagesTab = computed(() => {
        return props.isClientOwner || userStore.isPlatformAdmin;
    });

    async function handleOpenDrawer({tab}: {tab: FreelancerDrawerTab}) {
        activeTab.value = tab;
        await freelancerDrawer.value?.show();
    }

    function handleHideDrawer() {
        activeTab.value = undefined;
    }

    eventBus.on('showFreelancerDrawer', handleOpenDrawer);

    onBeforeUnmount(() => {
        eventBus.off('showFreelancerDrawer', handleOpenDrawer);
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .drawer {
        &__info-container {
            align-items: center;
        }

        &__about {
            margin-left: var(--joy-core-spacing-3);

            &__name {
                p {
                    @include text.getFontBySize(primary-450);

                    margin-right: var(--joy-core-spacing-2);
                    margin-bottom: 0;
                }

                a {
                    display: flex;
                    align-items: center;
                }
            }

            &__job-title {
                @include text.getFontBySize(primary-400);
            }
        }

        &_wrapper {
            display: flex;
            flex-flow: column nowrap;
            height: 100%;
        }
    }
</style>
